import React, { memo, useEffect, useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from "react-i18next";
import * as dayjs from 'dayjs';

import { usersList, removeInvite, clubUsersCount, invitesList } from "../../services/users";
import { RemoveUserDialog } from "../../components/removeUserDialog";
import { ExportUsersDialog } from "../../components/exportUsersButton";

import Table from "../../components/Table";
import { InviteMemberButton } from '../../components/InviteMember';
import { InviteCoachesButton } from '../../components/InviteCoaches';
import { SearchBar } from "../../components/FilterBar";
import { useNavigate } from "react-router-dom";

const CoachesPage = () => {
  const { t } = useTranslation();
  const usersComponent = useRef(null);
  const groupComponent = useRef(null);

  const [{ coaches_count, players_count, parents_count, invites_count }, setUsersAmount] = useState({})
  const [userType, setUserType] = useState('all')

  const getUsersAmount = async () => {
    const response = await clubUsersCount();
    setUsersAmount(response);
  }

  useEffect(() => { getUsersAmount() }, [])

  const reloadSubData = () => {
    getUsersAmount();
    if (usersComponent.current) { usersComponent.current.getCoaches() }
    if (groupComponent.current) { groupComponent.current.getInvites() }
  }

  const swtichTab = (tab) => {
    if (userType === tab) return;
    setUserType(tab);
  }

  return (
    <div className="team-details">
      <div className="team-details-header">
        <div className="team-details-header-text">{t('common.members')}</div>
        <div className="team-details-header-btn">
          { userType === "all" && <ExportUsersDialog />}
          { userType === "coach" && <InviteCoachesButton onClose={(reload) => reload && reloadSubData()} />}
          <InviteMemberButton onClose={(reload) => reload && reloadSubData()}/>
        </div>
      </div>
      <div className="team-details-tabs">
        <div className={`team-details-tabs-option pointer ${userType === "all" ? 'active' : ''}`} onClick={() => swtichTab('all')}>{t('common.all')}</div>
        <div className={`team-details-tabs-option pointer ${userType === "coach" ? 'active' : ''}`} onClick={() => swtichTab('coach')}>{t('teams.coaches', { count: coaches_count })}</div>
        <div className={`team-details-tabs-option pointer ${userType === 'player' ? 'active' : ''}`} onClick={() => swtichTab('player')}>{`${t('teams.players', { count: players_count })}`}</div>
        <div className={`team-details-tabs-option pointer ${userType === 'viewer' ? 'active' : ''}`} onClick={() => swtichTab('viewer')}>{`${t('teams.parents', { count: parents_count })} `}</div>
        <div className={`team-details-tabs-option pointer ${userType === "invites" ? 'active' : ''}`} onClick={() => swtichTab('invites')}>{t('coaches.pending', { count: invites_count })}</div>
      </div>
      <div className="team-details-list">
        { userType === 'invites' ? 
          <InvitesTable ref={groupComponent} onDataChange={reloadSubData} /> : 
          <UsersTable ref={usersComponent} user_type={userType} onDataChange={reloadSubData} /> 
        }
      </div>
    </div>
  )
};

export default memo(CoachesPage);

const UsersTable = forwardRef(({ user_type, onDataChange }, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [{ users, count }, setData] = useState({ users: [], count: 0 })
  const [filters, setFilters] = useState({ page: 1, per_page: 10, user_type: 'all' })
  const { page, per_page } = filters;

  const getCoaches = async () => {
    const response = await usersList(filters);
    setData(response)
  };

  useImperativeHandle(ref, () => ({ getCoaches}));

  useEffect(() => { getCoaches() }, [filters])
  useEffect(() => { setFilters({...filters, page: 1, user_type }) }, [user_type])

  const reload = async () => {
    await getCoaches();
    onDataChange(); 
  }

  const editCoach = (coach) => {
    navigate("/club_admin/users/" + coach?.id);
  }

  const pagination = useMemo(
    () => ({
      pageIndex: page - 1,
      pageSize: per_page,
    }),
    [page, per_page]
  )

  const handlePagination = ({ pageSize, pageIndex}) => {
    setFilters({...filters, page: pageIndex + 1, per_page: pageSize })
  }

  const tableState = { columnVisibility: { remove_at: user_type === "coach" }, pagination}

  const columns = useMemo(
    () => [
      {
        id: 'online',
        header: " ", cell: (info) =>
          <div className={info.row.original?.active ? "status-indicator status-indicator-active" : " status-indicator status-indicator-passive"}>&bull;</div>,  
          meta: { style: { width: 20 }}
      },
      { header: t('common.table_name'), accessorKey: 'name', cell: info => <div onClick={() => editCoach(info.row.original)}>
          {info.getValue()}
          {info.row.original?.is_manager ? <span className="admin-badge">Admin</span> : ''}
        </div> },
      { header: t('common.table_email'), accessorKey: 'email', cell: info => info.getValue() },
      { header: t('coaches.table_deadline'), accessorKey: 'remove_at', cell: info => info.getValue(), },
      { header: t('coaches.table_last'), accessorKey: 'last_seen_at', cell: info => info.getValue() && dayjs(info.getValue()).format('YYYY-MM-DD'), },
      {
        id: 'actions',
        header: " ", cell: (info) =>
          <div>
             <RemoveUserDialog user={info.row.original} onClose={(needReload) => needReload && reload()}><i className="icon icon-user-unfollow"></i></RemoveUserDialog>
            <i className="icon icon-settings" onClick={() => editCoach(info.row.original)}></i>
          </div>,  meta: { style: { width: 60 }}
      },
    ],
    [t]
  )

  return(
    <>
      <div className='filters-bar'>
          <SearchBar onChange={(q) => {setFilters({...filters, q})}} placeholder={t('lessons.placeholder')}/>
      </div>
      <Table {...{ data: users, columns, pageCount: Math.ceil(count / per_page), manualPagination: true, state: tableState, onPaginationChange: (e) => handlePagination(e(pagination)) }}/>
    </>
  )
});

const InvitesTable = React.forwardRef(({ onDataChange }, ref) => {
  const { t } = useTranslation();

  const [invites, setInvites] = useState([])

  const removeInvitation = async (invite) => {
    try {
      await removeInvite(invite.id);
      // Оновлюємо дані після видалення запрошення
      await getInvites();
      onDataChange(); // Оновлюємо лічильники
    } catch (error) {
      console.error('Error removing invite:', error);
    }
  }

  const getInvites = async () => {
    const response = await invitesList();
    setInvites(response.invites)
  };

  // Викриваємо метод getInvites через ref
  React.useImperativeHandle(ref, () => ({
    getInvites
  }));

  useEffect(() => { getInvites() }, [])

  const columns = React.useMemo(
    () => [
      { header: t('common.table_name'), accessorKey: 'name', cell: info => <div>
          {info.getValue()}
        </div> },
      { header: t('common.table_email'), accessorKey: 'email', cell: info => info.getValue() },
      { header: t('coaches.table_team'), accessorKey: 'team_name', cell: info => info.getValue() },
      { header: t('coaches.table_role'), accessorKey: 'role', cell: info => info.getValue() },
      { header: t('coaches.table_invite'), accessorKey: 'created_at', cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'), },
      {
        id: 'actions',
        header: " ", cell: (info) =>
          <div>
            <i className="icon icon-trash" onClick={() => confirm(t('coaches.confirm_remove_invite', { name:info.row.original?.email})) && removeInvitation(info.row.original)}></i>
          </div>,  meta: { style: { width: 60 }}
      },
    ],
    []
  )

  return(
    <Table {...{ data: invites, columns }}/>
  )
});